/**
 * usePartnerAssignments Hook
 * Manages expert assignments with Firebase
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
  getDocs
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { 
  PartnerAssignment, 
  AssignmentFormData, 
  AssignmentStatus,
  ExpertProfile 
} from '@/types/partner-assignments.types';
import { useToast } from '@/hooks/use-toast';

export function usePartnerAssignments(dealId?: string) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [assignments, setAssignments] = useState<PartnerAssignment[]>([]);
  const [experts, setExperts] = useState<ExpertProfile[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch assignments
  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    let q;
    if (dealId) {
      q = query(
        collection(db, 'partnerAssignments'),
        where('dealId', '==', dealId),
        orderBy('createdAt', 'desc')
      );
    } else {
      // Fetch all assignments where user is involved
      q = query(
        collection(db, 'partnerAssignments'),
        orderBy('createdAt', 'desc')
      );
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const assignmentsData = snapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          acceptedAt: doc.data().acceptedAt?.toDate(),
          completedAt: doc.data().completedAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
        }))
        .filter((a) => {
          // Filter based on user involvement
          return (
            a.expertId === user.id ||
            a.buyerId === user.id ||
            a.sellerId === user.id ||
            a.createdBy === user.id
          );
        }) as PartnerAssignment[];
      setAssignments(assignmentsData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching assignments:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.id, dealId]);

  // Fetch available experts
  useEffect(() => {
    const q = query(
      collection(db, 'expertProfiles'),
      where('availability', '!=', 'unavailable')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const expertsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      })) as ExpertProfile[];
      setExperts(expertsData);
    });

    return () => unsubscribe();
  }, []);

  // Create assignment
  const createAssignment = useCallback(async (data: AssignmentFormData): Promise<string | null> => {
    if (!user?.id) return null;

    try {
      const expert = experts.find(e => e.id === data.expertId);
      
      const assignmentData = {
        ...data,
        expertName: expert?.name,
        expertEmail: expert?.email,
        status: 'pending' as AssignmentStatus,
        createdAt: Timestamp.now(),
        createdBy: user.id,
      };

      const docRef = await addDoc(collection(db, 'partnerAssignments'), assignmentData);

      // Notify expert
      await addDoc(collection(db, 'userNotifications'), {
        userId: data.expertId,
        type: 'new_assignment',
        title: 'New Assignment',
        message: `You have been assigned to a new deal`,
        relatedAssignmentId: docRef.id,
        relatedDealId: data.dealId,
        createdAt: Timestamp.now(),
        seen: false,
      });

      toast({
        title: 'Expert Assigned',
        description: 'The expert has been notified.',
      });

      return docRef.id;
    } catch (error) {
      console.error('Error creating assignment:', error);
      toast({
        title: 'Error',
        description: 'Failed to assign expert.',
        variant: 'destructive',
      });
      return null;
    }
  }, [user?.id, experts, toast]);

  // Update assignment status
  const updateAssignmentStatus = useCallback(async (
    assignmentId: string, 
    status: AssignmentStatus
  ): Promise<boolean> => {
    try {
      const updateData: Record<string, unknown> = {
        status,
        updatedAt: Timestamp.now(),
      };

      if (status === 'accepted') {
        updateData.acceptedAt = Timestamp.now();
      } else if (status === 'completed') {
        updateData.completedAt = Timestamp.now();
      }

      await updateDoc(doc(db, 'partnerAssignments', assignmentId), updateData);

      const assignment = assignments.find(a => a.id === assignmentId);
      if (assignment) {
        // Notify relevant parties
        const notifyUsers = [assignment.createdBy];
        if (assignment.buyerId) notifyUsers.push(assignment.buyerId);
        if (assignment.sellerId) notifyUsers.push(assignment.sellerId);

        for (const userId of notifyUsers) {
          if (userId !== user?.id) {
            await addDoc(collection(db, 'userNotifications'), {
              userId,
              type: `assignment_${status}`,
              title: `Assignment ${status.charAt(0).toUpperCase() + status.slice(1)}`,
              message: `An expert assignment has been ${status}.`,
              relatedAssignmentId: assignmentId,
              relatedDealId: assignment.dealId,
              createdAt: Timestamp.now(),
              seen: false,
            });
          }
        }
      }

      toast({
        title: 'Status Updated',
        description: `Assignment has been ${status}.`,
      });

      return true;
    } catch (error) {
      console.error('Error updating assignment:', error);
      toast({
        title: 'Error',
        description: 'Failed to update assignment.',
        variant: 'destructive',
      });
      return false;
    }
  }, [assignments, user?.id, toast]);

  // Add note to assignment
  const addNote = useCallback(async (assignmentId: string, note: string): Promise<boolean> => {
    try {
      const assignment = assignments.find(a => a.id === assignmentId);
      const currentNotes = assignment?.notes || '';
      const timestamp = new Date().toISOString();
      const newNote = `[${timestamp}] ${user?.email}: ${note}`;
      
      await updateDoc(doc(db, 'partnerAssignments', assignmentId), {
        notes: currentNotes ? `${currentNotes}\n${newNote}` : newNote,
        updatedAt: Timestamp.now(),
      });

      toast({
        title: 'Note Added',
        description: 'Your note has been saved.',
      });

      return true;
    } catch (error) {
      console.error('Error adding note:', error);
      toast({
        title: 'Error',
        description: 'Failed to add note.',
        variant: 'destructive',
      });
      return false;
    }
  }, [assignments, user?.email, toast]);

  // Filter helpers
  const getActiveAssignments = useCallback(() => {
    return assignments.filter(a => 
      a.status === 'pending' || a.status === 'accepted' || a.status === 'in_progress'
    );
  }, [assignments]);

  const getCompletedAssignments = useCallback(() => {
    return assignments.filter(a => a.status === 'completed');
  }, [assignments]);

  const getExpertAssignments = useCallback((expertId: string) => {
    return assignments.filter(a => a.expertId === expertId);
  }, [assignments]);

  return {
    assignments,
    experts,
    loading,
    createAssignment,
    updateAssignmentStatus,
    addNote,
    getActiveAssignments,
    getCompletedAssignments,
    getExpertAssignments,
  };
}
